import type { GridProps } from '@mui/material';
import { Grid, Skeleton } from '@mui/material';
import type { ReactNode } from 'react';
import React from 'react';

type FormSectionSkeletonProps = {
  children: ReactNode;
};

export const FormSectionSkeleton = ({ children }: FormSectionSkeletonProps) => {
  // Default grid layout properties
  let gridContainerProps: GridProps = {
    container: true,
    spacing: 2,
    columnSpacing: 10,
    rowSpacing: 5,
    marginY: 2
  };

  // Find the first element which should be the FormContainer
  const formContainer = React.Children.toArray(children).find(React.isValidElement) as React.ReactElement | undefined;

  if (!formContainer) {
    return children;
  }

  // Find the Grid container
  const gridContainer = React.Children.toArray(formContainer.props.children || []).find(
    (child) => React.isValidElement(child) && child.type === Grid && child.props.container
  ) as React.ReactElement | undefined;

  // If no Grid container found, return the original children
  // This happens for components that don't use the standard Grid container/item structure
  // (Components like MedicalSummary and MedicalInformation handle their loading states internally)
  if (!gridContainer) {
    return children;
  }

  // Get the grid container props to ensure our skeletons have the same spacing and layout as the actual form elements
  const { spacing, columnSpacing, rowSpacing, marginY } = gridContainer.props;
  gridContainerProps = {
    container: true,
    spacing,
    columnSpacing,
    rowSpacing,
    marginY
  };

  // Get the grid container props to ensure our skeletons have the same spacing and layout as the actual form elements
  const gridItems = React.Children.toArray(gridContainer.props.children)
    .filter((child) => React.isValidElement(child) && child.type === Grid && child.props.item)
    .map((item, index) => {
      if (!React.isValidElement(item)) {
        return null;
      }
      const { xs, sm } = item.props;

      return (
        <Grid key={index} item xs={xs} sm={sm}>
          <Skeleton variant="rounded" height={56} />
        </Grid>
      );
    })
    .filter(Boolean);

  // If for some reason we find a Grid container but it has no Grid items,
  // fall back to the original children
  if (gridItems.length === 0) {
    return children;
  }

  // Create a skeleton version of the form that matches the current form
  return <Grid {...gridContainerProps}>{gridItems}</Grid>;
};
